/**
 * Comment anchors in the browser: a selection turned into what the server's
 * anchor accepts, and a stored anchor turned back into characters to highlight.
 *
 * WHAT THE SERVER ACCEPTS (packages/server/src/service/comments.ts,
 * `resolveAnchor`) is a quote and an optional `nth`: it scans every block of
 * the tab in document order, finds each place the NORMALISED quote occurs
 * (overlapping, one character apart), and takes the nth. It does not take a
 * block id. So "the most precise form it supports" is the quote plus the nth
 * computed HERE, from the block the selection is actually in -- never a bare
 * quote that leaves the server to pick the first of two identical phrases.
 *
 * That only works if this file counts exactly as the server does, so it uses
 * the server's own normaliser (`normalize.ts`, imported by path: the package
 * index also exports `hash.ts`, which needs node:crypto) and mirrors its scan
 * line for line. `anchors.test.ts` holds the two to each other.
 *
 * WHAT THE SERVER STORES is `{blockId, from, to, text}`, with `from`/`to` in
 * the block's NORMALISED text, and TASK-030 keeps them on the same words
 * through later edits. Highlighting maps them back to raw offsets with the same
 * normaliser, and refuses to highlight anything whose words are not the quoted
 * words -- the document and the comment list are fetched separately, so for a
 * moment after an edit one can be a revision ahead of the other, and a stale
 * range must not light up the wrong words.
 *
 * A GAP anchor (TASK-044) is a place between two top-level blocks, not words:
 * `{kind: 'gap', after, before, text}`, `text` being the server's label. It
 * has no `blockId`, so `highlightRanges` passes it over; `gapThreads` draws it
 * as a marker in its gap instead. The label is `gapLabel` from the schema
 * package, imported by path like the normaliser, so the page names a gap
 * exactly as the server will.
 *
 * A DIAGRAM (TASK-045) has no text on the page to select, only the labels
 * mermaid drew; its anchor is an ordinary one on its source, found through
 * the diagram key (`anchorForDiagram`, lib/diagram.ts).
 */

import { inlineText, normalizeForFind, normalizeWithMap, toRawRange } from '../../../schema/src/normalize.ts'
import { gapLabel, labelBetween } from '../../../schema/src/gaps.ts'
import { labelKey, sliceForKey } from './diagram'
import type { CommentRow, PMNode, ThreadState } from './types'

export { gapLabel }

/** `labelBetween` on the page's blocks: `null` for an edge of the tab. */
export function labelForGap(above: PMNode | null, below: PMNode | null): string {
  return labelBetween(above as any, below as any)
}

/* --------------------------------------------------------- the server's scan */

/** A block the server's anchor scan searches: one with a text child. */
export interface TextBlock {
  id: string
  node: PMNode
  /** Its text as the matcher builds it: chips contribute their display text. */
  raw: string
}

/**
 * Every block `resolveAnchor` would search, in the order it searches them:
 * depth-first, a node before its children, any node with a `text` child.
 * (A list item holds paragraphs, not text, so it is passed over and its
 * paragraphs are found underneath it.)
 */
export function textBlocks(content: PMNode[]): TextBlock[] {
  const out: TextBlock[] = []
  const scan = (nodes: PMNode[]) => {
    for (const n of nodes) {
      if (n.content && n.content.some((c) => c.type === 'text')) {
        out.push({ id: (n.attrs?.id as string | undefined) ?? '', node: n, raw: inlineText(n as any) })
      }
      if (n.content) scan(n.content)
    }
  }
  scan(content)
  return out
}

/** Every start of `needle` in `hay`, overlapping -- the server's loop exactly. */
function hitsIn(hay: string, needle: string): number[] {
  const out: number[] = []
  if (!needle) return out
  let from = 0
  for (;;) {
    const at = hay.indexOf(needle, from)
    if (at === -1) break
    out.push(at)
    from = at + 1
  }
  return out
}

/* --------------------------------------------------- selection -> anchor */

/** What `POST /v1/docs/:id/comments` takes as `anchor`, plus what we show. */
export interface TextAnchorDraft {
  kind?: 'find'
  /** The block the person selected in. Sent nowhere; used to show the quote. */
  blockId: string
  /** The quoted words, exactly as the document has them (trimmed). */
  text: string
  /** Which occurrence, 1-based, across the whole tab in the server's order. */
  nth: number
  /** How many times the words occur in the tab. 1 means `nth` is a formality. */
  occurrences: number
  /** The selection ran past this block; only this block's part is quoted. */
  clipped: boolean
  /**
   * Made in a rendered diagram (TASK-045): the label as displayed, or null for
   * the whole diagram. What the composer shows instead of `text`, which is
   * Mermaid source. Sent nowhere.
   */
  diagram?: { label: string | null }
}

/**
 * The place between two top-level blocks, chosen to comment on (TASK-044).
 * Sent as `{kind: 'gap', after, before}`; the label is for DISPLAY only and is
 * never sent -- the server computes the stored one from the same blocks.
 */
export interface GapAnchorDraft {
  kind: 'gap'
  after: string | null
  before: string | null
  label: string
}

export type AnchorDraft = TextAnchorDraft | GapAnchorDraft

const WS = /\s/

/**
 * A selection of raw characters `[start, end)` in one block, as an anchor.
 *
 * Null when there is nothing to anchor: an empty or whitespace-only selection,
 * or a block the server would not search. Leading and trailing whitespace is
 * trimmed first, so a triple-click that takes the paragraph's trailing newline
 * quotes the paragraph and nothing after it.
 */
export function anchorForSelection(
  blocks: TextBlock[],
  blockId: string,
  start: number,
  end: number,
  clipped = false,
): TextAnchorDraft | null {
  const index = blocks.findIndex((b) => b.id === blockId)
  if (index === -1) return null
  const block = blocks[index]
  let s = Math.max(0, Math.min(start, end))
  let e = Math.min(block.raw.length, Math.max(start, end))
  while (s < e && WS.test(block.raw[s])) s++
  while (e > s && WS.test(block.raw[e - 1])) e--
  if (s >= e) return null

  const text = block.raw.slice(s, e)
  const needle = normalizeForFind(text)
  if (!needle.trim()) return null

  const map = normalizeWithMap(block.raw)
  // The normalised character whose cluster holds the selection's first raw
  // character. A selection that begins inside a cluster (half a flag emoji)
  // begins at that cluster, which is where the server's offsets can land.
  let normStart = map.out.length
  for (let i = 0; i < map.out.length; i++) {
    if (map.ends[i] > s) {
      normStart = i
      break
    }
  }

  const here = hitsIn(map.out, needle)
  if (here.length === 0) return null
  // The occurrence that starts where the selection starts. When normalisation
  // shifts the boundary (a selection starting on the second of two spaces),
  // the nearest one is the one the person meant.
  let pick = here.indexOf(normStart)
  if (pick === -1) {
    let best = 0
    for (let i = 1; i < here.length; i++) {
      if (Math.abs(here[i] - normStart) < Math.abs(here[best] - normStart)) best = i
    }
    pick = best
  }

  let before = 0
  let total = 0
  for (let i = 0; i < blocks.length; i++) {
    const n = i === index ? here.length : hitsIn(normalizeForFind(blocks[i].raw), needle).length
    if (i < index) before += n
    total += n
  }

  return { blockId, text, nth: before + pick + 1, occurrences: total, clipped }
}

/**
 * A selection in a rendered diagram, as an anchor on its source (TASK-045).
 *
 * `label` is the `textContent` of the one label the selection lies in (null
 * for none, or several), `display` the same label as it reads. A label whose
 * key occurs exactly once in the diagram's key anchors to that slice of the
 * source; anything else anchors to the whole diagram. The slice goes through
 * `anchorForSelection` as OFFSETS, not text, so trimming and `nth` come from
 * the same code as any other selection -- and a label whose words also occur
 * elsewhere in the source (a node id, another block) is counted from where it
 * really is.
 */
export function anchorForDiagram(
  blocks: TextBlock[],
  blockId: string,
  label: string | null,
  display: string | null,
): TextAnchorDraft | null {
  const block = blocks.find((b) => b.id === blockId)
  if (!block) return null
  const slice = label ? sliceForKey(block.raw, labelKey(label)) : null
  if (slice) {
    const one = anchorForSelection(blocks, blockId, slice.from, slice.to)
    if (one) return { ...one, diagram: { label: display || null } }
  }
  const whole = anchorForSelection(blocks, blockId, 0, block.raw.length)
  return whole ? { ...whole, diagram: { label: null } } : null
}

/* ---------------------------------------------------- anchor -> highlight */

/** How a highlight is drawn. `detached` threads are never drawn at all. */
export type HighlightState = ThreadState

/** One thread's range in one block, in RAW offsets of the block's text. */
export interface BlockRange {
  threadId: string
  from: number
  to: number
  state: HighlightState
  /** The quoted words, for the control's accessible name. */
  text: string
}

/**
 * The live anchors of these threads, by block, ready to draw.
 *
 * Only thread ROOTS carry an anchor. A detached thread is left out (its words
 * are gone; the sidebar shows the quote struck through). So is any anchor
 * whose offsets no longer land on its own quoted words in the blocks we have
 * -- the transient state between a document fetch and a comment fetch. A gap
 * anchor has no block and is drawn by `gapThreads`; a gap answered into its
 * gap is a text anchor again, and drawn here.
 */
export function highlightRanges(blocks: TextBlock[], roots: CommentRow[]): Map<string, BlockRange[]> {
  const byId = new Map(blocks.map((b) => [b.id, b]))
  const out = new Map<string, BlockRange[]>()
  for (const row of roots) {
    const a = row.anchor as { blockId?: string; from?: number; to?: number; text?: string } | null
    if (!a?.blockId || typeof a.from !== 'number' || typeof a.to !== 'number' || !a.text) continue
    if (row.detached_at) continue
    const block = byId.get(a.blockId)
    if (!block) continue
    const map = normalizeWithMap(block.raw)
    if (a.from < 0 || a.to > map.out.length || a.from >= a.to) continue
    if (map.out.slice(a.from, a.to) !== normalizeForFind(a.text)) continue
    const raw = toRawRange(map, a.from, a.to)
    if (raw.from >= raw.to) continue
    const list = out.get(a.blockId) ?? []
    list.push({ threadId: row.id, from: raw.from, to: raw.to, state: row.state ?? 'open', text: a.text })
    out.set(a.blockId, list)
  }
  return out
}

/* ------------------------------------------------------------- gap markers */

/** The key a gap is found by, on both sides: its two neighbours, `''` for an edge. */
export function gapKey(after: string | null | undefined, before: string | null | undefined): string {
  return `${after ?? ''}|${before ?? ''}`
}

/** One thread pinned to a gap. */
export interface GapThread {
  threadId: string
  state: HighlightState
  /** The server's label, for the marker's accessible name. */
  label: string
}

/**
 * The live gap anchors of these threads, by gap key. As with text ranges, a
 * detached thread is left out, and a gap is only drawn where the page has a
 * gap with exactly those neighbours: while the document and the comment list
 * are a revision apart, a stale pair simply matches no gap.
 */
export function gapThreads(roots: CommentRow[]): Map<string, GapThread[]> {
  const out = new Map<string, GapThread[]>()
  for (const row of roots) {
    const a = row.anchor
    if (!a || a.kind !== 'gap' || row.detached_at) continue
    const key = gapKey(a.after, a.before)
    const list = out.get(key) ?? []
    list.push({ threadId: row.id, state: row.state ?? 'open', label: a.text ?? '' })
    out.set(key, list)
  }
  return out
}

/** The threads covering one run of characters, or a gap. */
export function coverOfThreads(threads: { threadId: string; state: HighlightState }[]): Cover | null {
  if (threads.length === 0) return null
  let state: HighlightState = threads[0].state
  for (const t of threads) if (RANK[t.state] > RANK[state]) state = t.state
  return { threads: threads.map((t) => t.threadId), state }
}

/* ------------------------------------------------ splitting for the renderer */

/** The threads covering one run of characters. */
export interface Cover {
  threads: string[]
  /** The most prominent state among them: open, then handled, then closed. */
  state: HighlightState
}

/** One piece of a block's inline content, after cutting at range boundaries. */
export type Piece =
  | { kind: 'inline'; node: PMNode; cover: Cover | null }
  /** A control for one thread, placed where its range ends. */
  | { kind: 'pin'; range: BlockRange }

const RANK: Record<HighlightState, number> = { open: 3, handled: 2, closed: 1 }

function coverOf(ranges: BlockRange[], from: number, to: number): Cover | null {
  const hit = ranges.filter((r) => r.from < to && r.to > from)
  if (hit.length === 0) return null
  let state: HighlightState = hit[0].state
  for (const r of hit) if (RANK[r.state] > RANK[state]) state = r.state
  return { threads: hit.map((r) => r.threadId), state }
}

/**
 * A block's inline children, with every text run cut where a highlight starts
 * or ends, and a pin after the piece where each range ends.
 *
 * The cut happens on the MODEL, before rendering, so a highlight is always the
 * innermost wrapper of a text run (inside its bold, its link, its strike) and
 * never an element that straddles two of them -- which is what keeps the
 * document's own marks intact. A chip is never cut; it is covered when a range
 * overlaps it.
 */
export function splitInlines(nodes: PMNode[], ranges: BlockRange[]): Piece[] {
  if (ranges.length === 0) return nodes.map((node) => ({ kind: 'inline', node, cover: null }))
  const pieces: Piece[] = []
  const pinned = new Set<string>()
  const pinAfter = (end: number, last: boolean) => {
    for (const r of ranges) {
      if (pinned.has(r.threadId)) continue
      if (r.to <= end || last) {
        pinned.add(r.threadId)
        pieces.push({ kind: 'pin', range: r })
      }
    }
  }

  let offset = 0
  nodes.forEach((node, i) => {
    const text = inlineText(node as any)
    const start = offset
    const end = start + text.length
    offset = end
    const last = i === nodes.length - 1

    if (node.type !== 'text' || !text) {
      pieces.push({ kind: 'inline', node, cover: text ? coverOf(ranges, start, end) : null })
      pinAfter(end, last)
      return
    }

    const cuts = new Set<number>([start, end])
    for (const r of ranges) {
      if (r.from > start && r.from < end) cuts.add(r.from)
      if (r.to > start && r.to < end) cuts.add(r.to)
    }
    const points = [...cuts].sort((a, b) => a - b)
    for (let k = 0; k < points.length - 1; k++) {
      const a = points[k]
      const b = points[k + 1]
      pieces.push({
        kind: 'inline',
        node: { ...node, text: text.slice(a - start, b - start) },
        cover: coverOf(ranges, a, b),
      })
      pinAfter(b, last && k === points.length - 2)
    }
  })
  // A block with no inline children at all still owes its pins.
  for (const r of ranges) if (!pinned.has(r.threadId)) pieces.push({ kind: 'pin', range: r })
  return pieces
}

/** Plain text (a code block's) cut the same way. */
export function splitText(text: string, ranges: BlockRange[]): { text: string; cover: Cover | null }[] {
  const cuts = new Set<number>([0, text.length])
  for (const r of ranges) {
    if (r.from > 0 && r.from < text.length) cuts.add(r.from)
    if (r.to > 0 && r.to < text.length) cuts.add(r.to)
  }
  const points = [...cuts].sort((a, b) => a - b)
  const out: { text: string; cover: Cover | null }[] = []
  for (let k = 0; k < points.length - 1; k++) {
    out.push({ text: text.slice(points[k], points[k + 1]), cover: coverOf(ranges, points[k], points[k + 1]) })
  }
  return out
}

/** A chip's length in the matcher's text, for mapping a DOM selection. */
export function inlineLength(node: PMNode): number {
  return inlineText(node as any).length
}
